import axios from 'axios';

class AxiosFactory {
  /** 请求方法 */
  _register_methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

  /**
   * 构造器
   * @param {Object} native_config axios原生配置项
   * @param {Object} custom_config 自定义配置
   */
  constructor(native_config, custom_config) {
    // 创建axios实例
    this._instance = axios.create(native_config);
    // 初始化自定义拦截器
    this._interceptors = custom_config?.interceptors;
    // 初始化默认拦截器
    this._initInterceptors();
    // 注册请求方法
    this._dispatchRegisterMethods();
  }

  /** 初始化默认拦截器 */
  _initInterceptors() {
    // 全局请求拦截器
    this._instance.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => Promise.reject(error)
    );
    // 实例请求拦截器
    this._instance.interceptors.request.use(
      this._interceptors?.requestInterceptors,
      this._interceptors?.requestInterceptorsCatch
    );
    // 实例响应拦截器
    this._instance.interceptors.response.use(
      this._interceptors?.responseInterceptors,
      this._interceptors?.responseInterceptorsCatch
    );
    // 全局响应拦截器保证最后执行
    this._instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * request请求方法
   * @param {Object} native_config axios原生配置项
   * @param {Object} custom_config 自定义配置
   * @returns {Promise<object>}
   */
  request(native_config, custom_config) {
    return new Promise((resolve, reject) => {
      // 如果我们为单个请求设置拦截器，这里使用单个请求的拦截器
      const config_aop = this._httpRequestInterceptorsAop(native_config, custom_config);
      // 执行axios的request请求
      this._instance.request(config_aop)
        .then((response) => {
          // 单个响应设置拦截器
          const response_aop = this._httpResponseInterceptorsAop(response, custom_config);
          resolve(response_aop);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * 实例单个请求拦截切面
   * @param {Object} native_config axios原生配置项
   * @param {Object} custom_config 自定义配置
   * @returns {Object} 请求参数
   */
  _httpRequestInterceptorsAop(native_config, custom_config) {
    if (custom_config?.interceptors?.requestInterceptors) {
      return custom_config.interceptors.requestInterceptors(native_config);
    }
    return native_config;
  }

  /**
   * 实例单个响应拦截切面
   * @param {Object} native_response axios原生响应数据
   * @param {Object} custom_config 自定义配置项
   * @returns {Object} 响应参数
   */
  _httpResponseInterceptorsAop(native_response, custom_config) {
    // 单个请求拦截优先级大于返回原生响应数据
    if (custom_config?.interceptors?.responseInterceptors) {
      return custom_config.interceptors.responseInterceptors(native_response);
    }
    // 返回原生响应数据
    if (custom_config?.is_return_native_response) {
      return native_response;
    }
    // 返回响应数据
    return native_response.data;
  }

  /** 注册请求方法 */
  _dispatchRegisterMethods() {
    this._register_methods.forEach(method => {
      const method_name = method.toLowerCase();
      this[method_name] = (config, request_config) => {
        return this.request({ ...config, method }, request_config);
      };
    });
  }
}

export default AxiosFactory;
